import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Gift } from "lucide-react";
import { toast } from "sonner";
import { getRewards, saveRewards, redeemReward } from "@/lib/rewards";
import { errorText } from "@/lib/family";

const RewardRow = ({ r, i, onChange, onRemove }) => (
  <li className="pp-reward-row" data-testid={`reward-edit-row-${i}`}>
    <input className="pp-input pp-reward-title" value={r.title} maxLength={60} onChange={(e) => onChange({ ...r, title: e.target.value })} data-testid={`reward-title-${i}`} />
    <label className="pp-reward-num"><img src="/acorn-t.png" alt="" />
      <input type="number" min={0} value={r.acorns} onChange={(e) => onChange({ ...r, acorns: Math.max(0, Number(e.target.value)) })} data-testid={`reward-acorns-${i}`} />
    </label>
    <label className="pp-reward-num gold">golden
      <input type="number" min={0} value={r.golden} onChange={(e) => onChange({ ...r, golden: Math.max(0, Number(e.target.value)) })} data-testid={`reward-golden-${i}`} />
    </label>
    <button className="pp-icon" onClick={onRemove} aria-label="Remove reward" data-testid={`reward-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
  </li>
);

export const RewardsEditor = ({ pin, profiles }) => {
  const [rewards, setRewards] = useState(null);
  const [childId, setChildId] = useState("");
  const [rewardId, setRewardId] = useState("");
  const [giving, setGiving] = useState(false);
  useEffect(() => { getRewards().then((d) => setRewards(d.rewards)); }, []);
  useEffect(() => {
    if (rewards && window.location.hash === "#rewards") document.getElementById("rewards")?.scrollIntoView({ behavior: "smooth" });
  }, [rewards]);
  const saved = useMemo(() => (rewards || []).filter((r) => r.id), [rewards]);
  if (!rewards) return null;

  const set = (i, r) => setRewards(rewards.map((x, j) => (j === i ? r : x)));
  const save = async () => {
    try { setRewards((await saveRewards(pin, rewards.filter((r) => r.title.trim()))).rewards); toast.success("Rewards saved"); }
    catch (e) { toast.error(errorText(e)); }
  };
  const given = async () => {
    setGiving(true);
    try { await redeemReward(pin, childId, rewardId); toast.success("Reward marked as given. Enjoy!"); setRewardId(""); }
    catch (e) { toast.error(errorText(e)); }
    setGiving(false);
  };

  return (
    <section id="rewards" className="pp-card" data-testid="rewards-editor">
      <p className="pp-label">Parents only · you're in charge</p>
      <h2 className="pp-h2">Family rewards</h2>
      <p className="opacity-75 mt-1">Set what each reward costs in acorns and golden acorns. Kids see this list and their progress.</p>
      <ul className="mt-4 grid gap-2">
        {rewards.map((r, i) => <RewardRow key={r.id || i} r={r} i={i} onChange={(x) => set(i, x)} onRemove={() => setRewards(rewards.filter((_, j) => j !== i))} />)}
      </ul>
      <div className="pp-actions">
        <button className="pp-ghost" onClick={() => setRewards([...rewards, { title: "New reward", acorns: 10, golden: 0 }])} data-testid="reward-add-button"><Plus className="h-4 w-4" /> Add reward</button>
        <button className="pp-primary" onClick={save} data-testid="reward-save-button"><Save className="h-5 w-5" /> Save rewards</button>
      </div>
      {profiles.length > 0 && (
        <div className="pp-reward-give mt-6" data-testid="reward-give-panel">
          <p className="pp-label">Reward earned? Mark it as given</p>
          <div className="flex flex-wrap gap-2 items-center">
            <select className="pp-voice-select" value={childId} onChange={(e) => setChildId(e.target.value)} data-testid="reward-give-child">
              <option value="">Choose child</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select className="pp-voice-select" value={rewardId} onChange={(e) => setRewardId(e.target.value)} data-testid="reward-give-reward">
              <option value="">Choose reward</option>
              {saved.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
            <button className="pp-ghost" disabled={giving || !childId || !rewardId} onClick={given} data-testid="reward-give-button"><Gift className="h-4 w-4" /> Mark given</button>
          </div>
          <p className="text-xs opacity-70 mt-2">This spends the acorns from their savings. Their lifetime jar total stays the same.</p>
        </div>
      )}
    </section>
  );
};
